import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../store';
import { useMsal } from '@azure/msal-react';
import { TokenHelper } from '../TokenHelper';
import { useTranslation } from 'react-i18next';
import { ConsultantActionCreators } from '../store/consultants';
import { dataActionCreators, DataState } from '../store/Data';
import { TeamsState } from '../store/TeamsState';
import FilesDragAndDrop from './Tools/FilesDragAndDrop/FilesDragAndDrop';
import { useEditorStyles, useStackClassName } from './Styles/Editor';
import { Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Field, Label, Link, Switch, SwitchOnChangeData, Toast, ToastTitle, ToastTrigger, useToastController } from '@fluentui/react-components';
import { useCustomers } from './Tools/ObjectLoader';

export const FileUploader = () => {
    // All data needed for edition should be loaded before calling the editor
    const data = useSelector<ApplicationState, DataState>((state: ApplicationState) => state.data);
    const teamsState = useSelector<ApplicationState, TeamsState>((state: ApplicationState) => state.teamsState);
    const { customers } = useCustomers();
    // Gen hooks
    const dispatch = useDispatch();
    const { instance } = useMsal();
    const { t } = useTranslation();
    const styles = useEditorStyles();
    const { dispatchToast, updateToast } = useToastController("NeoToasterUnique");
    // Fields validation
    const [file, setFile] = React.useState<File>();
    const [confirmOverwrive, setConfirmOverwrive] = React.useState<boolean>(false);
    // Form validation
    const [isSubmitValid, setSubmitValid] = React.useState(false);
    const NeedConfirmation = React.useMemo(() => {
        return (data.FileUpload &&
            (
                (data.FileUpload.invoice && data.FileUpload.invoice.invoiceSharePointFileUniqueId !== null)
                || (data.FileUpload.timeSheetEntry && data.FileUpload.timeSheetEntry.timeSheetFileExist)
                || (data.FileUpload.sale && data.FileUpload.sale.orderSharePointFileUniqueId != null)
                || (data.FileUpload.consultantInvoice && data.FileUpload.consultantInvoice.sharePointFileUniqueId != null)
                || (data.FileUpload.projectAssigment && data.FileUpload.projectAssigment.orderSharePointFileUniqueId != null)
            )
        );
    }, [data]);

    React.useEffect(() => {
        const submitValid = file !== undefined;
        setSubmitValid(submitValid);
    }, [file]);

    if (data.FileUpload === null) {
        return null;
    }

    const submit = () => {
        if (file && data.FileUpload && data.FileUpload) {
            const c = new FormData();
            let req: string;
            c.append("formFile", file);
            if (data.FileUpload.timeSheetEntry && data.FileUpload.timeSheetEntry.timesheetEntryid) {
                c.append("TimesheetEntryid", data.FileUpload.timeSheetEntry.timesheetEntryid);
                req = "api/TimeSheetFile";
            }
            else if (data.FileUpload.sale && data.FileUpload.sale.sellid) {
                c.append("Saleid", data.FileUpload.sale.sellid);
                req = "api/SalesFile";
            }
            else if (data.FileUpload.invoice && data.FileUpload.invoice.invoiceid) {
                c.append("Invoiceid", data.FileUpload.invoice.invoiceid);
                req = "api/Invoices/" + data.FileUpload.invoice.invoiceid + "/UploadInvoiceFile";
            }
            else if (data.FileUpload.consultantInvoice && data.FileUpload.consultantInvoice.consultantInvoiceid) {
                c.append("ConsultantInvoiceid", data.FileUpload.consultantInvoice.consultantInvoiceid);
                req = "api/ConsultantsInvoicesFile";
            }
            else if (data.FileUpload.projectAssigment && data.FileUpload.projectAssigment.projectAssignmentid) {
                c.append("ProjectAssignmentid", data.FileUpload.projectAssigment.projectAssignmentid);
                req = "api/ProjectsAssigmentsFile";
            }
            TokenHelper.GetToken(instance, teamsState)
                .then(token => {
                    if (token !== null) {
                        const toastId = (Math.random() + 1).toString(36).substring(7);
                        dispatchToast(
                            <Toast appearance="inverted">
                                <ToastTitle>
                                    Saving...
                                </ToastTitle>
                            </Toast>,
                            { intent: "info", timeout: -1, position: "top-end", toastId: toastId }
                        );
                        fetch(req, {
                            method: "POST",
                            headers: {
                                authorization: "bearer " + token,
                            },
                            body: c,
                            mode: "cors",
                            cache: "default",
                        })
                            .then(response => {
                                if (response.status === 200) {
                                    updateToast({
                                        content: (
                                            <Toast appearance="inverted">
                                                <ToastTitle action={
                                                    <ToastTrigger>
                                                        <Link>Dismiss</Link>
                                                    </ToastTrigger>
                                                }>
                                                    Saved
                                                </ToastTitle>
                                            </Toast>
                                        ),
                                        intent: "success",
                                        toastId,
                                        timeout: 5000,
                                    });
                                    if (data.FileUpload && data.FileUpload.timeSheetEntry) {
                                        data.FileUpload.timeSheetEntry.timeSheetFileExist = true;
                                        dispatch(dataActionCreators.patchTimeSheetEntryEdition({ ...data.FileUpload.timeSheetEntry }));
                                    }
                                    else if (data.FileUpload && data.FileUpload.sale) {
                                        data.FileUpload.sale.orderSharePointFileUniqueId = "uploaded";
                                        dispatch(dataActionCreators.patchSaleEdition({ ...data.FileUpload.sale }));
                                    }
                                    else if (data.FileUpload && data.FileUpload.invoice) {
                                        data.FileUpload.invoice.invoiceSharePointFileUniqueId = "uploaded";
                                        dispatch(dataActionCreators.patchInvoiceEdition({ ...data.FileUpload.invoice }));
                                    }
                                    else if (data.FileUpload && data.FileUpload.consultantInvoice) {
                                        data.FileUpload.consultantInvoice.sharePointFileUniqueId = "uploaded";
                                        data.FileUpload.consultantInvoice.invoiceFileExist = true;
                                        dispatch(ConsultantActionCreators.patchConsultantInvoiceEdition({ ...data.FileUpload.consultantInvoice }));
                                    }
                                    else if (data.FileUpload && data.FileUpload.projectAssigment) {
                                        data.FileUpload.projectAssigment.orderSharePointFileUniqueId = "uploaded";
                                        dispatch(dataActionCreators.patchProjectAssigmentEdition({ ...data.FileUpload.projectAssigment }));
                                    }

                                    dispatch(dataActionCreators.requestFileUploadClose());
                                }
                                else {
                                    updateToast({
                                        content: (
                                            <Toast appearance="inverted">
                                                <ToastTitle action={
                                                    <ToastTrigger>
                                                        <Link>Dismiss</Link>
                                                    </ToastTrigger>
                                                }>
                                                    Save failed
                                                </ToastTitle>
                                            </Toast>
                                        ),
                                        intent: "error",
                                        toastId,
                                        timeout: 10000,
                                    });
                                }
                            });
                    }
                });
        }
    };

    const projectAssignment = data.FileUpload.timeSheetEntry
        && data.FileUpload.timeSheetEntry.projectAssignmentid
        && data.projectsAssigments
        ? data.projectsAssigments.find(x => data.FileUpload && data.FileUpload.timeSheetEntry && x.projectAssignmentid === data.FileUpload.timeSheetEntry.projectAssignmentid)
        : undefined;
    const customer = data.FileUpload.sale
        && data.FileUpload.sale.customerid
        && customers
        ? customers.find(x => data.FileUpload && data.FileUpload.sale && x.customerid === data.FileUpload.sale.customerid)
        : undefined;

    let allowedFormats: string[] = ['jpg', 'png', 'pdf', 'xlsx', 'docx'];
    if (data.FileUpload.invoice
        && (data.FileUpload.invoice.invoiceid || data.FileUpload.consultantInvoice || data.FileUpload.sale || data.FileUpload.projectAssigment)
    ) {
        // Invoices only in word (docx and pdf)
        allowedFormats = ['pdf', 'docx'];
    }

    return <Dialog
        open
        modalType="modal">
        <DialogSurface>
            <DialogBody>
                <DialogTitle>{t('UploadFile')}</DialogTitle>
                <DialogContent>
                    <form
                        onSubmit={() => {
                            dispatch(dataActionCreators.requestFileUploadClose());
                        }}
                        className={useStackClassName()}
                        autoComplete="off"
                    >
                        {projectAssignment &&
                            <Field label={t('ProjectsAssigment')} >
                                <Label className={styles.label}>{projectAssignment.displayName}</Label>
                            </Field>
                        }
                        {customer &&
                            <Field label="Sale for" >
                                <Label className={styles.label}>{customer.displayName}</Label>
                            </Field>
                        }
                        {data.FileUpload.invoice
                            && data.FileUpload.invoice.invoiceRef &&
                            <Field label={t('Invoice')} >
                                <Label className={styles.label}>{data.FileUpload.invoice.invoiceRef}</Label>
                            </Field>
                        }
                        {data.FileUpload.consultantInvoice
                            && data.FileUpload.consultantInvoice.invoiceRef &&
                            <Field label={t('Invoice')} >
                                <Label className={styles.label}>{data.FileUpload.consultantInvoice.invoiceRef}</Label>
                            </Field>
                        }
                        {data.FileUpload.projectAssigment
                            && data.FileUpload.projectAssigment.displayName &&
                            <Field label={t('ProjectsAssigments')} >
                                <Label className={styles.label}>{data.FileUpload.projectAssigment.displayName}</Label>
                            </Field>
                        }

                        <FilesDragAndDrop
                            onUpload={(files) => {
                                if (files.length === 1) {
                                    setFile(files[0]);
                                }
                            }}
                            count={1}
                            formats={allowedFormats}
                            containerStyles={{
                                width: '100%',
                                height: '200px',
                                border: '2px #c3c3c3 dashed',
                                borderRadius: '12px',
                            }}
                            openDialogOnClick
                            successText=""
                            successTime={0}
                        >
                            <div className={styles.ColFullDragDrop}>
                                <Label className={styles.label}>{t('ClickDropFileHere')}</Label>
                                {file !== undefined && <Label className={styles.label}>{file.name}</Label>}
                            </div>
                        </FilesDragAndDrop>
                        {NeedConfirmation &&
                            <Switch label={t('ConfirmFileOverWrite')}
                                checked={confirmOverwrive}
                                onChange={(ev: React.ChangeEvent<HTMLInputElement>, data: SwitchOnChangeData) => { setConfirmOverwrive(data.checked); }} />
                        }
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => dispatch(dataActionCreators.requestFileUploadClose())}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => submit()}
                        type="submit"
                        form="my-form-id"
                        appearance="primary"
                        disabled={(NeedConfirmation ? !confirmOverwrive : false) || !isSubmitValid}
                    >
                        {t("Save")}
                    </Button>
                </DialogActions>
            </DialogBody>
        </DialogSurface>
    </Dialog>;
};